import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import ChooseSeatedTicketTypes from './ChooseSeatedTicketTypes';
import ChooseSeatedAddOns from './ChooseSeatedAddOns';
import TicketTypeInventoryStats from '../../../shared/TicketTypeInventoryStats';

import { Badge, Button, HelpCircle, Select, Table, TextInput, Tooltip } from '@opendate-app/setlist';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import MaskedInput from 'react-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

import {
  MIN_TICKETS,
  MAX_TICKETS
} from '../constants/confirmAttendeesConstants';
import { parentHeader } from '../../../shared/seatsioUtils';
import { listedBesideSeatMap } from '../../WebOrderForm/utils/seatSelection';

const calculateTotalTickets = (ticketTypes, addOns) => {
  var admissionTicketsCount = ticketTypes.reduce((sum, tt) => {
    return sum + (tt.quantity || 0);
  }, 0);

  var addOnTicketsCount = addOns.reduce((sum, ao) => {
    return sum + (ao.quantity || 0);
  }, 0);

  return (
    admissionTicketsCount
      + addOnTicketsCount
  );
}

const canSubmitForm = (orderType, ticketTypes, addOns, ticketReservation, holdExceeded) => {
  var totalTicketsCount = calculateTotalTickets(ticketTypes, addOns);

  return (
    orderType
      && orderType.length > 0
      && totalTicketsCount >= MIN_TICKETS
      && totalTicketsCount <= MAX_TICKETS
      && ticketTypes.every((tt) => !tt.quantity || tt.quantity === 0 || tt.face_value.length > 0)
      && addOns.every((ao) => !ao.quantity || ao.quantity === 0 || ao.face_value.length > 0)
      && Object.keys(ticketReservation).length > 0
      && !holdExceeded
  );
}

const holdItems = (hold) => hold.items || [{ ticket_type_id: hold.ticket_type_id, available: hold.available }];

const quantityOf = (ticketTypes, ticketTypeId) => (ticketTypes.find((tt) => tt.id === ticketTypeId) || {}).quantity || 0;

const exceededItem = (hold, ticketTypes) => (
  hold && holdItems(hold).find((item) => quantityOf(ticketTypes, item.ticket_type_id) > item.available)
);

const exceedsHold = (holds, revealedHoldIds, ticketTypes) => (
  !!exceededItem(holds.find((h) => revealedHoldIds.includes(h.id)), ticketTypes)
);

const invalidQuantity = (ticketTypes, addOns) => {
  var totalTicketsCount = calculateTotalTickets(ticketTypes, addOns);

  return (
      totalTicketsCount > MAX_TICKETS
  );
}

const selectedObjectLabel = (object) => {
  var ownLabel;

  switch(object.objectType) {
  case "Seat":
    ownLabel = "Seat";
    break;
  case "Table":
    ownLabel = "Table";
    break;
  case "GeneralAdmissionArea":
    ownLabel = "Area";
    break;
  default:
    ownLabel = object.labels.displayedLabel;
  }

  var labelParts = [];

  if(object.labels.section){
    labelParts.push("Section " + object.labels.section);
  }

  if(object.labels.parent){
    labelParts.push(parentHeader(object) + " " + object.labels.parent);
  }

  if(object.labels.own){
    labelParts.push(ownLabel + " " + object.labels.own);
  }

  return labelParts.join(", ");
}

const selectedObjectType = (object) => {
  var objectType;

  switch(object.objectType) {
  case "GeneralAdmissionArea":
    objectType = "AddOn";
    break;
  default:
    objectType = object.objectType;
  }

  return objectType;
}

function sortAddOns(a, b) {
  const firstCategoryKey = a.seating_chart_category_key;
  const nextCategoryKey = b.seating_chart_category_key;

  if (firstCategoryKey === null && nextCategoryKey !== null) {
    return -1;
  }

  if (firstCategoryKey !== null && nextCategoryKey === null) {
    return 1;
  }

  if (firstCategoryKey === null && nextCategoryKey === null) {
    return a.position - b.position;
  }

  return a.name.localeCompare(b.name);
}

var debouncedBuildTicketReservation;
const AddAttendees = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  orderType,
  orderTypeChanged,
  ticketTypes,
  addOns,
  ticketTypesChanged,
  addOnsChanged,
  buildTicketReservation,
  ticketReservation,
  createTicketReservation,
  manualTypes,
  seatsIOPublicKey,
  seatsIOChart,
  seatsIOChartChanged,
  seatsIOSelectedObjects,
  seatsIOSelectedObjectsChanged,
  seatingChartEvents,
  seatingEventHoldsByEventId,
  generalAdmissionHolds,
  selectedGeneralAdmissionHoldId,
  generalAdmissionHoldSelected,
}) => {
  const selectedHold = generalAdmissionHolds.find((hold) => (
    hold.id === selectedGeneralAdmissionHoldId &&
      holdItems(hold).some((item) => quantityOf(ticketTypes, item.ticket_type_id) > 0)
  ));
  const revealedHoldIds = selectedHold ? [selectedHold.id] : [];
  const heldTicketTypeIds = selectedHold ? holdItems(selectedHold).map((item) => item.ticket_type_id) : [];
  const holdExceeded = exceedsHold(generalAdmissionHolds, revealedHoldIds, ticketTypes);

  useEffect(() => {
    debouncedBuildTicketReservation = _.debounce((csrfToken, team, confirm, orderType, ticketTypes, addOns, revealedHoldIds) => {
      if (exceedsHold(generalAdmissionHolds, revealedHoldIds, ticketTypes)) {
        return;
      }

      buildTicketReservation(csrfToken, team, confirm, orderType, ticketTypes, addOns, revealedHoldIds);
    }, 250);
  }, []);

  const holdRowsFor = (ticketType) => (
    confirm.seating_chart_id ? [] : generalAdmissionHolds
      .flatMap((hold) => holdItems(hold)
        .filter((item) => item.ticket_type_id === ticketType.id)
        .map((item) => ({ ...ticketType, kind: "hold", id: `${hold.id}-${ticketType.id}`, hold, item, ticketType })))
  );

  const rows = [
    ...[...ticketTypes].sort((a, b) => a.position - b.position).flatMap((tt) => [{ ...tt, kind: "ticketType" }, ...holdRowsFor(tt)]),
    ...[...addOns].sort(sortAddOns).map((ao) => ({ ...ao, kind: "addOn" })),
  ];

  const patchHoldRow = (row, patch) => {
    const updated = ticketTypes.map((tt) => tt.id === row.ticketType.id ? Object.assign({}, tt, patch) : tt);
    const inUse = holdItems(row.hold).some((item) => quantityOf(updated, item.ticket_type_id) > 0);
    const holdIds = inUse ? [row.hold.id] : [];

    generalAdmissionHoldSelected(inUse ? row.hold.id : null);
    ticketTypesChanged(updated);
    debouncedBuildTicketReservation(csrfToken, team, confirm, orderType, updated, addOns, holdIds);
  };

  const holdRowSelected = (row) => !!selectedHold && selectedHold.id === row.hold.id;

  const isHeldTicketRow = (row) => row.kind === "ticketType" && heldTicketTypeIds.includes(row.id);

  const lockedNote = (row) => {
    if (!selectedHold) {
      const openItem = row.kind === "hold" && row.item.available > 0 &&
        holdItems(row.hold).find((item) => quantityOf(ticketTypes, item.ticket_type_id) > 0);
      const openName = openItem && (ticketTypes.find((tt) => tt.id === openItem.ticket_type_id) || {}).name;
      return openItem ? `Clear open ${openName} to use this hold.` : null;
    }

    if (isHeldTicketRow(row)) {
      return `Using ${selectedHold.name}. Add open ${row.name} in a separate order.`;
    }

    if (row.kind === "hold" && !holdRowSelected(row)) {
      return "One hold per order.";
    }

    return null;
  };

  const patchRow = (row, patch) => {
    var list = row.kind === "ticketType" ? ticketTypes : addOns;
    var updated = list.map((item) => item.id === row.id ? Object.assign({}, item, patch) : item);

    if (row.kind === "ticketType") {
      if (!selectedHold && selectedGeneralAdmissionHoldId) {
        generalAdmissionHoldSelected(null);
      }
      ticketTypesChanged(updated);
      debouncedBuildTicketReservation(csrfToken, team, confirm, orderType, updated, addOns, revealedHoldIds);
    } else {
      addOnsChanged(updated);
      debouncedBuildTicketReservation(csrfToken, team, confirm, orderType, ticketTypes, updated, revealedHoldIds);
    }
  };

  const quantityDisabled = (row) => {
    if (lockedNote(row)) {
      return true;
    }

    if (row.kind === "hold") {
      return row.item.available <= 0 && !holdRowSelected(row);
    }

    if (row.kind === "addOn") {
      return !!(row.seating_chart_category_key && row.seating_chart_category_key.length > 0);
    }

    return !!confirm.seating_chart_id && !listedBesideSeatMap(row);
  };

  const quantityValue = (row) => {
    if (row.kind === "hold") {
      return holdRowSelected(row) ? (row.ticketType.quantity || "") : "";
    }

    if (isHeldTicketRow(row)) {
      return "";
    }

    return row.quantity || "";
  };

  const quantityError = (row) => {
    if (holdExceeded && row.kind === "hold" && holdRowSelected(row) && row.item.available < (row.ticketType.quantity || 0)) {
      return `${selectedHold.name} only has ${row.item.available} ${row.ticketType.name} left`;
    }

    if (lockedNote(row)) {
      return undefined;
    }

    return invalidQuantity(ticketTypes, addOns) || undefined;
  };

  const faceValueDisabled = (row) => !!lockedNote(row) || (row.kind === "hold" && !holdRowSelected(row));

  const rowLabel = (row) => {
    if (row.kind !== "hold") return row.name;
    return holdItems(row.hold).length > 1 ? `${row.hold.name} hold (${row.ticketType.name})` : `${row.hold.name} hold`;
  };

  const changeRow = (row, patch) => (row.kind === "hold" ? patchHoldRow(row, patch) : patchRow(row, patch));

  const columns = [
    {
      key: "name",
      header: "Ticket Type",
      primary: true,
      render: (row) => (
        row.kind === "hold" ? (
          <span title={row.hold.name} style={{ paddingLeft: "20px" }}>
            {row.hold.name}
            <Badge className="ml-2" size="sm">Hold</Badge>
          </span>
        ) : <span title={row.name}>{row.name}</span>
      ),
    },
    {
      key: "inventory",
      header: "Inventory",
      render: (row) => row.kind === "hold" ? <div>{row.item.available} left</div> : (
        <div>
          <div>{row.available}</div>
          <TicketTypeInventoryStats item={row} className="small text-muted mt-1" />
        </div>
      ),
    },
    { key: "price", header: "Price", render: (row) => <span className="text-muted">{row.price_to_currency}</span> },
    {
      key: "quantity",
      header: "Quantity",
      width: 110,
      render: (row) => (
        <TextInput as={MaskedInput}
                   size="sm"
                   aria-label={`${rowLabel(row)} quantity`}
                   style={{"textAlign": "right"}}
                   error={quantityError(row)}
                   hint={lockedNote(row) || undefined}
                   mask={createNumberMask({ prefix: "", decimalLimit: 0, integerLimit: 3 })}
                   placeholder="0"
                   disabled={quantityDisabled(row)}
                   value={quantityValue(row)}
                   onChange={(e) => changeRow(row, { quantity: parseInt(e.target.value || "0") })} />
      ),
    },
    {
      key: "face_value",
      header: (
        <span className="d-inline-flex align-items-center">
          Face Value
          <Tooltip title="Face Value"
                   supportingText="You can modify the Face Value to display a different value on the customer's ticket. This does not change the actual price of the ticket for settlement purposes."
                   maxWidth={240}>
            <span className="ml-1 d-inline-flex" tabIndex={0} aria-label="About face value">
              <HelpCircle width={14} height={14} aria-hidden="true" />
            </span>
          </Tooltip>
        </span>
      ),
      width: 150,
      render: (row) => (
        <TextInput as={CurrencyInput}
                   size="sm"
                   aria-label={`${rowLabel(row)} face value`}
                   leadingText="$"
                   style={{"textAlign": "right"}}
                   error={(quantityValue(row) > 0 && !row.face_value) || undefined}
                   maskOptions={{ prefix: "", integerLimit: null }}
                   placeholder="0"
                   disabled={faceValueDisabled(row)}
                   value={faceValueDisabled(row) ? "" : (row.face_value || "")}
                   onChange={(e) => changeRow(row, { face_value: e.target.value })} />
      ),
    },
  ];

  return (
    <div>
      <div className="row">
        <div className="col-12">
          <p style={{"fontSize": "20px", "marginBottom": "3px"}}>
            <strong>Add Attendees</strong>
          </p>
          <p>
            Manually add attendee for comp tickets or box office payments
          </p>
          <hr style={{
                "borderTop": "2px solid var(--od-c-f1f1f1, #f1f1f1)",
                "margin": "30px 0px 25px 0px"
              }} />
        </div>
      </div>
      <form onSubmit={
              (e) => {
                e.preventDefault();

                if(!canSubmitForm(orderType, ticketTypes, addOns, ticketReservation, holdExceeded)){
                  return false;
                }

                createTicketReservation(csrfToken, team, confirm, orderType, ticketTypes, addOns, seatsIOChart, revealedHoldIds);
              }
            }>
        <div className="form-row">
          <div className="col-12 col-lg-6 col-xl-4">
            <Select label="Order Type"
                    id="ticket-reservation-manual-type"
                    options={manualTypes.map((option) => ({ value: option, label: option }))}
                    value={orderType || ""}
                    onChange={
                      (updated) => {
                        orderTypeChanged(updated);
                        debouncedBuildTicketReservation(csrfToken, team, confirm, updated, ticketTypes, addOns, revealedHoldIds);
                      }
                    } />
          </div>
        </div>
        {confirm.seating_chart_id ? (
          <div className="form-row">
            <div className="col-12" style={{"marginTop": "38px"}}>
              <label className="mb-2" style={{"fontSize": "18px"}}>
                <strong>Choose Admission Tickets</strong>
              </label>
              <ChooseSeatedTicketTypes team={team}
                                       csrfToken={csrfToken}
                                       confirm={confirm}
                                       ticketTypes={ticketTypes}
                                       ticketTypesChanged={ticketTypesChanged}
                                       addOns={addOns}
                                       seatsIOPublicKey={seatsIOPublicKey}
                                       seatsIOChart={seatsIOChart}
                                       seatsIOChartChanged={seatsIOChartChanged}
                                       seatsIOSelectedObjects={seatsIOSelectedObjects}
                                       seatsIOSelectedObjectsChanged={seatsIOSelectedObjectsChanged}
                                       selectedObjectLabel={selectedObjectLabel}
                                       selectedObjectType={selectedObjectType}
                                       orderType={orderType}
                                       staffOrder
                                       buildTicketReservation={buildTicketReservation}
                                       eventHolds={seatingEventHoldsByEventId[confirm.admission_seating_chart_event_id]} />
            </div>
          </div>
        ) : null}
        <div className="form-row">
          <div className="col-12" style={{"marginTop": "38px"}}>
            <Table columns={columns}
                   data={rows}
                   stackBelow={640}
                   getRowKey={(row) => `${row.kind}-${row.id}`} />
            <div className="d-flex justify-content-end align-items-center mt-3">
              <strong className="mr-3">Total Value</strong>
              <div style={{"width": "160px"}}>
                <TextInput size="sm"
                           aria-label="Total value"
                           leadingText="$"
                           disabled
                           style={{"textAlign": "right"}}
                           value={
                             Object.keys(ticketReservation).length > 0 && !holdExceeded ? (
                               formatter
                                 .format(ticketReservation.total)
                                 .replace("$", "")
                             ) : ""
                           }
                           placeholder="0" />
              </div>
            </div>
          </div>
        </div>
        {confirm.add_on_seating_chart_id ? (
          <div className="form-row">
            <div className="col-12" style={{"marginTop": "38px"}}>
              <label className="mb-2" style={{"fontSize": "18px"}}>
                <strong>Choose Add-on Tickets</strong>
              </label>
              <ChooseSeatedAddOns team={team}
                                  csrfToken={csrfToken}
                                  confirm={confirm}
                                  ticketTypes={ticketTypes}
                                  addOns={addOns}
                                  addOnsChanged={addOnsChanged}
                                  buildTicketReservation={buildTicketReservation}
                                  seatsIOPublicKey={seatsIOPublicKey}
                                  orderType={orderType}
                                  selectedObjectLabel={selectedObjectLabel}
                                  selectedObjectType={selectedObjectType}
                                  eventHolds={seatingEventHoldsByEventId[confirm.add_on_seating_chart_event_id]} />
            </div>
          </div>
        ) : null}
        {seatingChartEvents.map((seatingChartEvent, index) => (
          <div className="form-row" key={index}>
            <div className="col-12" style={{"marginTop": "15px"}}>
              <label className="mb-2" style={{"fontSize": "16px"}}>
                <strong>{seatingChartEvent.name}</strong>
              </label>
              <ChooseSeatedAddOns team={team}
                                  csrfToken={csrfToken}
                                  confirm={confirm}
                                  ticketTypes={ticketTypes}
                                  addOns={addOns}
                                  addOnsChanged={addOnsChanged}
                                  buildTicketReservation={buildTicketReservation}
                                  seatsIOPublicKey={seatsIOPublicKey}
                                  orderType={orderType}
                                  selectedObjectLabel={selectedObjectLabel}
                                  checkoutGroupItem={{
                                    item_id: seatingChartEvent.id,
                                    extra: { seatsio_event_id: seatingChartEvent.seatsio_event_id }
                                  }}
                                  selectedObjectType={selectedObjectType}
                                  eventHolds={seatingEventHoldsByEventId[seatingChartEvent.id]} />
            </div>
          </div>
        ))}
        <div className="form-row text-right">
          <div className="col-12"
               style={{"marginTop": "25px"}}>
            <Button variant="primary"
                    type="submit"
                    disabled={!canSubmitForm(orderType, ticketTypes, addOns, ticketReservation, holdExceeded)}>
              Next
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

AddAttendees.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  orderType: PropTypes.string,
  orderTypeChanged: PropTypes.func.isRequired,
  ticketTypes: PropTypes.array,
  addOns: PropTypes.array,
  ticketTypesChanged: PropTypes.func.isRequired,
  addOnsChanged: PropTypes.func.isRequired,
  buildTicketReservation: PropTypes.func.isRequired,
  ticketReservation: PropTypes.object,
  createTicketReservation: PropTypes.func.isRequired,
  manualTypes: PropTypes.array,
  seatsIOPublicKey: PropTypes.string,
  seatsIOChart: PropTypes.object,
  seatsIOChartChanged: PropTypes.func.isRequired,
  seatsIOSelectedObjects: PropTypes.array,
  seatsIOSelectedObjectsChanged: PropTypes.func.isRequired,
  seatingChartEvents: PropTypes.array,
  seatingEventHoldsByEventId: PropTypes.object,
  selectedGeneralAdmissionHoldId: PropTypes.number,
  generalAdmissionHoldSelected: PropTypes.func.isRequired,
  generalAdmissionHolds: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    ticket_type_id: PropTypes.number,
    available: PropTypes.number.isRequired,
  })),
};

AddAttendees.defaultProps = {
  generalAdmissionHolds: [],
  selectedGeneralAdmissionHoldId: null,
};

export default AddAttendees;
